<template>
  <div>
    <!-- Title -->
    <Title />

    <!-- Main Card -->
    <v-card class="mx-auto mt-5" tile>
      <v-list shaped>

        <!-- Farm Selection -->
        <v-list-item>
          <v-row>
            <v-col cols="12" md="10">
              <h4>Farm:</h4>
              <v-select v-model="currentFarmId" :items="farmList" item-value="id" item-title="name" label="Select Farm"
                @change="changeFarm" solo dense class="selection">
                <template v-slot:item="{ item }">
                  <div class="d-flex align-center">
                    <img :src="item.avatar" class="avatar mr-2" />
                    <span>{{ item.name }} - {{ item.active_pond_count }} Active</span>
                  </div>
                </template>
                <template v-slot:selection="{ item }">
                  <div class="d-flex align-center">
                    <img :src="item.avatar" class="avatar mr-2" />
                    <span>{{ item.name }} - {{ item.active_pond_count }} Active</span>
                  </div>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-list-item>

        <!-- Loading Spinner -->
        <v-container fluid class="d-flex justify-center align-center">
          <v-progress-circular v-if="loadingStatus" indeterminate color="primary" size="32"></v-progress-circular>
        </v-container>

        <!-- Pond Selection -->
        <v-list-item v-if="currentFarmId && !loadingStatus">
          <v-row>
            <v-col cols="12" md="10">
              <h4>Pond:</h4>
              <v-select v-model="cycleId" :items="pondList" item-value="id" item-title="name" label="Select Pond"
                @change="changePond" solo dense class="pond-select" />
            </v-col>
          </v-row>
        </v-list-item>

        <!-- Weight Table -->
        <v-list-item v-if="cycleId">
          <v-row>
            <v-col cols="12" md="10">
              <h4>Existing Weights</h4>
              <v-simple-table class="mt-0">
                <thead>
                  <tr>
                    <th class="text-center custom-header">Weight Sample Date</th>
                    <th class="text-center custom-header">Individual Shrimp Weight (g)</th>
                    <th class="text-center custom-header"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in filteredWeights" :key="item.id || index">
                    <td class="text-center">{{ formatDate(item.weight_date) }}</td>
                    <td class="text-center">{{ item.weight }}</td>
                    <td class="text-center">
                      <v-btn v-if="item.weight" small color="red" class="white--text"
                        @click="confirmDeleteWeight(index)">
                        Delete
                      </v-btn>
                    </td>
                  </tr>

                  <!-- Row for Adding New -->
                  <tr v-if="addingNew">
                    <td>
                      <v-menu v-model="datePickerMenu" :close-on-content-click="false" transition="scale-transition"
                        offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field v-model="newWeightDate" label="Select Date" readonly dense hide-details solo
                            v-bind="attrs" v-on="on" />
                        </template>
                        <v-date-picker v-model="newWeightDate" @input="datePickerMenu = false" :max="today" />
                      </v-menu>
                    </td>
                    <td class="text-center">
                      <v-text-field v-model="newWeight" type="text" label="Input Weight" hide-details="auto" dense solo
                        :error-messages="errorMessages" @input="validateWeight" />
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </v-simple-table>

              <!-- Button Controls -->
              <v-btn small color="primary" class="mt-2" @click="toggleAddWeight">
                {{ addWeightButtonLabel }}
              </v-btn>
              <v-btn v-if="addingNew" small color="primary" class="mt-2 ml-2" @click="saveWeight">
                Save
              </v-btn>
            </v-col>
          </v-row>
        </v-list-item>

      </v-list>
    </v-card>
  </div>
</template>

<script>
import { mapState } from "vuex";
import Title from '@/components/Title.vue';
import tokenAxios from "@/components/tokenAxios";
import moment from "moment";
import Swal from "sweetalert2";

export default {
  components: { Title },
  computed: {
    ...mapState(["isMobile", "user_info", "isMinnowtech"]),
    filteredWeights() {
      return this.weightList
        .filter(item => item.weight !== null)
        .sort((a, b) => new Date(a.weight_date) - new Date(b.weight_date));
    },
    filteredActivePonds() {
      return this.pondList.filter(item => item.completed === null);
    },
    addWeightButtonLabel() {
      return this.addingNew ? 'Cancel' : 'Add New Weight';
    },
    today() {
      return new Date().toISOString().substr(0, 10);
    },
  },
  created() {
    this.fetchFarms();
  },
  data() {
    return {
      API_SERVER: process.env.VUE_APP_API_SERVER,
      currentFarmId: null,
      farmList: [],
      pondList: [],
      weightList: [],
      addingNew: false,
      newWeight: null,
      errorMessages: [],
      newWeightDate: null,
      cycleId: null,
      stockingDate: null,
      loadingStatus: false,
      datePickerMenu: false,
    };
  },
  methods: {
    // Validation
    validateWeight() {
      this.errorMessages = [];
      if (!this.newWeight) return;
      if (this.newWeight >= 100) {
        this.errorMessages.push('Weight must be less than 100');
      }
    },

    // Fetch Farms
    async fetchFarms(page = 1) {
      await this.withLoading(async () => {
        const response = await tokenAxios(this.user_info.access_token).get(`${this.API_SERVER}/farm/farm/?format=json&page=${page}`);
        if (response.status === 200 && response.data.length) {
          this.farmList = response.data
            .filter(farm => farm.status === 1)
            .map(o => ({
              ...o,
              avatar: o.avatar || "https://farm-avatar.s3.amazonaws.com/minnowtech.jpg",
            }));
        }
      });
    },

    changeFarm(farm) {
      this.resetData();
      this.fetchPonds(farm);
    },

    // Fetch Ponds
    async fetchPonds(farm) {
      await this.withLoading(async () => {
        const response = await tokenAxios(this.user_info.access_token).get(`${this.API_SERVER}/farm/pond/?status=1&no_page=1&format=json&farm=${farm}`);
        if (response.status === 200 && response.data.length) {
          this.pondList = response.data
            .filter(o => o.completed === null)
            .map(o => ({
              ...o,
              text: `Pond ${o.pond_id} : Stocked on ${o.date}`,
            }));
        }
      });
    },

    changePond(cycleId) {
      const selected = this.pondList.find(p => p.id === cycleId);
      if (selected) this.stockingDate = selected.date;
      this.weightList = [];
      this.fetchWeights(cycleId);
    },

    // Fetch Weights
    async fetchWeights(cycleId) {
      await this.withLoading(async () => {
        const response = await tokenAxios(this.user_info.access_token).get(`${this.API_SERVER}/farm/results/?format=json&pond=${cycleId}`);
        if (response.status === 200 && response.data.results.length) {
          this.weightList = response.data.results;
        }
      });
    },

    toggleAddWeight() {
      this.resetForm();
      this.addingNew = !this.addingNew;
    },

    async saveWeight() {
      this.validateWeight();
      if (this.errorMessages.length) {
        Swal.fire("Invalid input!", this.errorMessages.join("\n"), "error");
        return;
      }

      const woc = this.calculateWOC();
      const formData = new FormData();
      formData.append("pond", this.cycleId);
      formData.append("woc", woc);
      formData.append("weight", this.newWeight);
      formData.append("weight_date", this.newWeightDate);

      await this.withLoading(async () => {
        try {
          const response = await tokenAxios(this.user_info.access_token).post(`${this.API_SERVER}/farm/results/saveWeight/`, formData);
          console.log(response.status);
          if (response.status === 201) {
            await this.fetchWeights(this.cycleId);
            Swal.fire("Success!", "Weight added successfully.", "success");
            this.resetForm();
          }
        } catch {
          Swal.fire("Error!", "Failed to add weight.", "error");
        }
      });
    },

    confirmDeleteWeight(index) {
      if (confirm("Are you sure you want to delete this weight?")) {
        this.deleteWeight(index);
      }
    },

    deleteWeight(index) {
      const weightId = this.weightList[index].id;
      this.weightList.splice(index, 1);
      tokenAxios(this.user_info.access_token)
        .delete(`${this.API_SERVER}/farm/results/${weightId}/`)
        .catch(error => {
          console.error('Error deleting weight:', error);
        });
    },

    calculateWOC() {
      const doc = moment(this.newWeightDate).diff(moment(this.stockingDate), 'days');
      return Math.floor((doc - 1) / 7) + 1;
    },

    formatDate(date) {
      return moment(date).format("DD/MM/YYYY");
    },

    resetData() {
      this.weightList = [];
      this.pondList = [];
      this.cycleId = null;
    },

    resetForm() {
      this.newWeight = null;
      this.newWeightDate = null;
      this.errorMessages = [];
    },

    async withLoading(callback) {
      this.loadingStatus = true;
      try {
        await callback();
      } finally {
        this.loadingStatus = false;
      }
    },
  },
};
</script>

<style scoped>
.avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
}

.selection .v-input__slot {
  background-color: #edfeee !important;
}
</style>
